import React from 'react';
import { View, StyleSheet, TouchableOpacity, Platform, Text as RNText } from 'react-native';
import { TextInput, Card, Text, RadioButton, List, Chip, Button, Checkbox } from 'react-native-paper';
import { colors, spacing, typography } from '../constants/theme';
import { PhotoCapture } from './PhotoCapture';
import StylusTextInput from './StylusTextInput';
import PlusMinusInput from './PlusMinusInput';
import DimensionInput from './DimensionInput';
import HelpfulLinksDisplay from './HelpfulLinksDisplay';
import { RoomDropdown } from './RoomDropdown';
import { FloorDropdown } from './FloorDropdown';
import { isMultiplicityQuestion } from '../utils/questionHelpers';
import { MAX_PHOTOS_PER_UPLOAD } from '../constants/photoLimits';
import { sanitizeDecimalInput, isCompleteDecimalValue, parseSanitizedDecimal } from '../utils/numberInput';
import type { ReviewFinding } from '../types';
import { FindingEditor } from './FindingEditor';
import { getFinding, replaceFinding } from '../utils/reviewFindings';

interface FormField {
  id: string;
  type: 'text' | 'textarea' | 'number' | 'decimal' | 'plusminus' | 'email' | 'tel' | 'date' | 'select' | 'multi_select' | 'radio' | 'checkbox' | 'file' | 'helpful_links' | 'dimensions_2d' | 'dimensions_3d';
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: string[] | Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
  deprecated?: boolean;
  deprecatedAt?: string;
  deprecatedBy?: number;
  accept?: string;
  multiple?: boolean;
  name?: string;
  triggersMultiplicity?: boolean; // For number fields: does value > 1 create multiple item instances?
  links?: Array<{ url: string; label: string }>; // For helpful_links type
  unit?: 'in' | 'ft' | 'cm' | 'm'; // For dimensions types
  historicallyVisible?: boolean;
}

interface QuestionsSchema {
  fields: FormField[];
}

interface DynamicFormProps {
  schema: QuestionsSchema;
  data: Record<string, any>;
  onChange: (data: Record<string, any>) => void;
  showDeactivated?: boolean;
  reviewId?: number;
  responseId?: number;
  responseTempId?: string;
  itemId?: number;
  initialPhotos?: Record<string, any[]>;
  onPhotosChange?: (questionId: string, photos: any[]) => void;
  itemName?: string;
  propertyId?: number;
  roomId?: number;
  floor?: string;
  onRoomSelect?: (roomId: number | undefined, itemIndex?: number) => void;
  onFloorSelect?: (floor: string | undefined, itemIndex?: number) => void;
  autoFocusOnMount?: boolean; // Controls whether text inputs auto-focus on mount
  requiresLocation?: boolean; // Whether this item requires location/room selection
  requiresFloor?: boolean; // Whether this item requires floor selection
  findingQuestionIds?: string[];
  findings?: ReviewFinding[];
  onFindingsChange?: (findings: ReviewFinding[]) => void;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  schema,
  data,
  onChange,
  showDeactivated = false,
  reviewId,
  responseId,
  responseTempId,
  itemId,
  initialPhotos = {},
  onPhotosChange,
  itemName = "Item",
  propertyId,
  roomId,
  floor,
  onRoomSelect,
  onFloorSelect,
  autoFocusOnMount = false,
  requiresLocation = true, // Default to true for backward compatibility
  requiresFloor = true, // Default to true for backward compatibility
  findingQuestionIds = [],
  findings = [],
  onFindingsChange,
}) => {
  const [decimalDrafts, setDecimalDrafts] = React.useState<Record<string, string>>({});

  const decimalFieldSignature = React.useMemo(() => {
    if (!schema?.fields) return '';
    return schema.fields
      .filter((field) => field.type === 'decimal')
      .map((field) => field.id)
      .sort()
      .join('|');
  }, [schema]);

  React.useEffect(() => {
    // Reset decimal drafts whenever the backing data changes (e.g., switching items)
    setDecimalDrafts({});
  }, [decimalFieldSignature, itemId, responseId]);

  // Early return if no fields
  if (!schema?.fields || !Array.isArray(schema.fields)) {
    return (
      <View style={styles.container}>
        <RNText style={{ color: '#666', fontStyle: 'italic', padding: 10 }}>
          No questions configured for this item
        </RNText>
      </View>
    );
  }

  const updateField = (fieldId: string, value: any) => {
    onChange({
      ...data,
      [fieldId]: value,
    });
  };

  const renderField = (field: FormField, itemIndex: number | null = null) => {
    const baseFieldKey = field.id;
    const fieldKey = itemIndex !== null ? `${baseFieldKey}_item_${itemIndex}` : baseFieldKey;
    const rawValue = data[fieldKey];
    const value = rawValue !== undefined && rawValue !== null ? rawValue : '';
    const fieldLabel = itemIndex !== null && itemCount > 1 ?
      `${itemName} ${itemIndex + 1} - ${field.label}` :
      field.label;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <StylusTextInput
            key={fieldKey}
            mode="outlined"
            label={fieldLabel + (field.required ? ' *' : '')}
            value={value}
            onChangeText={(text) => updateField(fieldKey, text)}
            placeholder={field.placeholder}
            keyboardType={field.type === 'email' ? 'email-address' : field.type === 'tel' ? 'phone-pad' : 'default'}
            style={styles.input}
            autoFocusOnMount={autoFocusOnMount}
            testID={`dynamic-input-${fieldKey}`}
          />
        );

      case 'textarea':
        return (
          <StylusTextInput
            key={fieldKey}
            mode="outlined"
            label={fieldLabel + (field.required ? ' *' : '')}
            value={value}
            onChangeText={(text) => updateField(fieldKey, text)}
            placeholder={field.placeholder}
            multiline
            numberOfLines={4}
            style={styles.input}
            autoFocusOnMount={autoFocusOnMount}
            testID={`dynamic-input-${fieldKey}`}
          />
        );

      case 'number':
        return (
          <StylusTextInput
            key={fieldKey}
            mode="outlined"
            label={fieldLabel + (field.required ? ' *' : '')}
            value={value?.toString() || ''}
            onChangeText={(text) => {
              const numValue = parseInt(text, 10);
              if (!isNaN(numValue) || text === '') {
                updateField(fieldKey, text === '' ? undefined : numValue);
              }
            }}
            placeholder={field.placeholder}
            keyboardType={Platform.OS === 'ios' ? 'phone-pad' : 'number-pad'}
            style={styles.input}
            autoFocusOnMount={autoFocusOnMount}
            testID={`dynamic-input-${fieldKey}`}
          />
        );

      case 'decimal': {
        const decimalValue =
          decimalDrafts[fieldKey] ??
          (rawValue !== undefined && rawValue !== null ? rawValue.toString() : '');

        const handleDecimalChange = (text: string) => {
          const sanitized = sanitizeDecimalInput(text);

          if (!sanitized) {
            setDecimalDrafts((prev) => {
              if (!(fieldKey in prev)) return prev;
              const updated = { ...prev };
              delete updated[fieldKey];
              return updated;
            });
            updateField(fieldKey, undefined);
            return;
          }

          if (isCompleteDecimalValue(sanitized)) {
            setDecimalDrafts((prev) => {
              if (!(fieldKey in prev)) return prev;
              const updated = { ...prev };
              delete updated[fieldKey];
              return updated;
            });
          } else {
            setDecimalDrafts((prev) => ({ ...prev, [fieldKey]: sanitized }));
          }

          const numericValue = parseSanitizedDecimal(sanitized);
          updateField(fieldKey, numericValue);
        };

        return (
          <StylusTextInput
            key={fieldKey}
            mode="outlined"
            label={fieldLabel + (field.required ? ' *' : '')}
            value={decimalValue}
            onChangeText={handleDecimalChange}
            placeholder={field.placeholder || 'Enter decimal value'}
            keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
            style={styles.input}
            autoFocusOnMount={autoFocusOnMount}
            testID={`dynamic-input-${fieldKey}`}
          />
        );
      }

      case 'plusminus': {
        const numericValue = typeof rawValue === 'number' ? rawValue : (parseInt(rawValue, 10) || 0);
        return (
          <PlusMinusInput
            key={fieldKey}
            label={fieldLabel + (field.required ? ' *' : '')}
            value={numericValue}
            onValueChange={(numValue) => updateField(fieldKey, numValue)}
            min={field.min ?? 0}
            max={field.max}
            disabled={false}
            style={styles.input}
            autoFocusOnMount={autoFocusOnMount}
          />
        );
      }

      case 'date':
        return (
          <StylusTextInput
            key={fieldKey}
            mode="outlined"
            label={fieldLabel + (field.required ? ' *' : '')}
            value={value}
            onChangeText={(text) => updateField(fieldKey, text)}
            placeholder="YYYY-MM-DD"
            style={styles.input}
            autoFocusOnMount={autoFocusOnMount}
            testID={`dynamic-input-${fieldKey}`}
          />
        );

      case 'radio':
      case 'select':
        const radioOptions = Array.isArray(field.options) 
          ? field.options.map(opt => typeof opt === 'string' ? { value: opt, label: opt } : opt)
          : [];
        
        return (
          <View key={fieldKey} testID={`dynamic-field-${fieldKey}`} style={styles.selectContainer}>
            <Text style={styles.selectLabel}>
              {fieldLabel + (field.required ? ' *' : '')}
            </Text>
            <RadioButton.Group
              onValueChange={(selectedValue) => updateField(fieldKey, selectedValue)}
              value={value}
            >
              {radioOptions.map((option) => (
                <List.Item
                  key={option.value}
                  testID={`dynamic-option-${fieldKey}-${option.value}`}
                  title={option.label}
                  left={() => <RadioButton value={option.value} />}
                  onPress={() => updateField(fieldKey, option.value)}
                  style={styles.radioItem}
                />
              ))}
            </RadioButton.Group>
          </View>
        );

      case 'multi_select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <View key={fieldKey} testID={`dynamic-field-${fieldKey}`} style={styles.selectContainer}>
            <Text style={styles.selectLabel}>
              {fieldLabel + (field.required ? ' *' : '')}
            </Text>
            <View style={styles.multiSelectContainer}>
              {field.options?.map((option) => {
                const optionObj = typeof option === 'string' ? { value: option, label: option } : option;
                const isSelected = selectedValues.includes(optionObj.value);
                return (
                  <Chip
                    key={optionObj.value}
                    testID={`dynamic-option-${fieldKey}-${optionObj.value}`}
                    mode={isSelected ? 'flat' : 'outlined'}
                    selected={isSelected}
                    onPress={() => {
                      const newValues = isSelected
                        ? selectedValues.filter(v => v !== optionObj.value)
                        : [...selectedValues, optionObj.value];
                      updateField(fieldKey, newValues);
                    }}
                    style={styles.chip}
                  >
                    {optionObj.label}
                  </Chip>
                );
              })}
            </View>
          </View>
        );

      case 'checkbox':
        const selectedCheckboxValues = Array.isArray(value) ? value : [];
        return (
          <View key={fieldKey} testID={`dynamic-field-${fieldKey}`} style={styles.selectContainer}>
            <Text style={styles.selectLabel}>
              {fieldLabel + (field.required ? ' *' : '')}
            </Text>
            <View style={styles.checkboxContainer}>
              {field.options?.map((option) => {
                const optionObj = typeof option === 'string' ? { value: option, label: option } : option;
                const isChecked = selectedCheckboxValues.includes(optionObj.value);
                return (
                  <TouchableOpacity
                    key={optionObj.value}
                    testID={`dynamic-option-${fieldKey}-${optionObj.value}`}
                    onPress={() => {
                      const newValues = isChecked
                        ? selectedCheckboxValues.filter(v => v !== optionObj.value)
                        : [...selectedCheckboxValues, optionObj.value];
                      updateField(fieldKey, newValues);
                    }}
                    style={styles.checkboxOption}
                  >
                    <Checkbox.Android
                      status={isChecked ? 'checked' : 'unchecked'}
                      onPress={() => {
                        const newValues = isChecked
                          ? selectedCheckboxValues.filter(v => v !== optionObj.value)
                          : [...selectedCheckboxValues, optionObj.value];
                        updateField(fieldKey, newValues);
                      }}
                      color={colors.primary}
                    />
                    <Text style={styles.checkboxLabel}>{optionObj.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        );

      case 'file':

        // Check if this is an image upload field and we have required props
        // For image uploads, use PhotoCapture component
        if ((field.accept?.includes('image') || field.type === 'file') && reviewId && (responseId || responseTempId) && itemId && onPhotosChange) {
          return (
            <View key={fieldKey} testID={`dynamic-photo-field-${fieldKey}`} style={styles.photoFieldContainer}>
              <PhotoCapture
                reviewId={reviewId}
                responseId={responseId}
                responseTempId={responseTempId}
                itemId={itemId}
                questionId={fieldKey}
                questionLabel={fieldLabel}
                initialPhotos={initialPhotos[fieldKey] || []}
                onPhotosChange={(photos: any[]) => onPhotosChange(fieldKey, photos)}
                maxPhotos={field.multiple !== false ? MAX_PHOTOS_PER_UPLOAD : 1}
              />
            </View>
          );
        }

        // If we have the basic props but missing some, still show PhotoCapture with limited functionality
        if (field.type === 'file') {
          return (
            <View key={fieldKey} testID={`dynamic-photo-field-${fieldKey}`} style={styles.photoFieldContainer}>
              <PhotoCapture
                reviewId={reviewId || 0}
                responseId={responseId || 0}
                responseTempId={responseTempId}
                itemId={itemId || 0}
                questionId={fieldKey}
                questionLabel={fieldLabel}
                initialPhotos={[]}
                onPhotosChange={() => {}}
                maxPhotos={field.multiple !== false ? MAX_PHOTOS_PER_UPLOAD : 1}
              />
            </View>
          );
        }
        
        // Fallback for non-image files or when photo upload props are missing
        return (
          <View key={fieldKey} testID={`dynamic-field-${fieldKey}`} style={styles.selectContainer}>
            <Text style={styles.selectLabel}>
              {fieldLabel + (field.required ? ' *' : '')}
            </Text>
            <Button
              testID={`dynamic-file-button-${fieldKey}`}
              mode="outlined"
              onPress={() => {
                // Placeholder - will implement actual file selection later
                alert('File upload feature coming soon. For now, this is just a placeholder.');
                updateField(fieldKey, `placeholder_file_${Date.now()}.jpg`);
              }}
              icon="camera"
              style={styles.fileButton}
            >
              {value ? `File selected: ${value}` : `Select ${field.multiple ? 'Files' : 'File'}`}
            </Button>
            {value && (
              <Text style={styles.fileInfo}>
                {field.accept?.includes('image') ? '📷' : '📄'} {value}
              </Text>
            )}
          </View>
        );

      case 'helpful_links':
        // Helpful links don't have user input - they're informational only
        return (
          <HelpfulLinksDisplay
            key={fieldKey}
            label={fieldLabel}
            links={field.links || []}
            style={styles.input}
          />
        );

      case 'dimensions_2d': {
        const dim2dValue = typeof rawValue === 'object' && rawValue !== null
          ? rawValue
          : { length: 0, width: 0 };
        return (
          <DimensionInput
            key={fieldKey}
            label={fieldLabel + (field.required ? ' *' : '')}
            value={dim2dValue}
            onValueChange={(dimValue) => updateField(fieldKey, dimValue)}
            unit={field.unit || 'in'}
            is3D={false}
            min={field.min ?? 0}
            max={field.max}
            disabled={false}
            style={styles.input}
          />
        );
      }

      case 'dimensions_3d': {
        const dim3dValue = typeof rawValue === 'object' && rawValue !== null
          ? rawValue
          : { length: 0, width: 0, depth: 0 };
        return (
          <DimensionInput
            key={fieldKey}
            label={fieldLabel + (field.required ? ' *' : '')}
            value={dim3dValue}
            onValueChange={(dimValue) => updateField(fieldKey, dimValue)}
            unit={field.unit || 'in'}
            is3D={true}
            min={field.min ?? 0}
            max={field.max}
            disabled={false}
            style={styles.input}
          />
        );
      }

      default:
        return null;
    }
  };

  // Filter deactivated fields unless showDeactivated is true
  // Also filter out room/location fields since we use RoomDropdown component
  // Keep fields with historicallyVisible flag even if deprecated (for historical reviews)
  const fieldsToShow = showDeactivated
    ? (schema.fields || [])
    : (schema.fields || []).filter(field => !field.deprecated || field.historicallyVisible);

  // Further filter out room/location fields to avoid duplication with RoomDropdown
  const filteredFields = fieldsToShow.filter(field => {
    const fieldName = field.name?.toLowerCase() || '';
    const fieldLabel = field.label?.toLowerCase() || '';
    const shouldFilter = (
      fieldName === 'room' ||
      fieldName === 'location' ||
      fieldName === 'roomlocation' ||
      fieldLabel.includes('room/location') ||
      fieldLabel.includes('location/room')
    );
    return !shouldFilter;
  });

  // Find multiplicity questions and other questions
  const multiplicityQuestions = filteredFields.filter(isMultiplicityQuestion);
  const otherQuestions = filteredFields.filter(field => !isMultiplicityQuestion(field));

  // Get the count from the first multiplicity question
  const primaryNumberQuestion = multiplicityQuestions[0];
  const primaryNumberKey = primaryNumberQuestion?.id;
  const itemCount = primaryNumberKey ? parseInt(data[primaryNumberKey] || '0', 10) : 0;

  const allElements: React.ReactElement[] = [];
  const renderQuestionFinding = (field: FormField) => {
    if (!onFindingsChange || !findingQuestionIds.includes(field.id)) {
      return null;
    }

    return (
      <FindingEditor
        questionId={field.id}
        finding={getFinding(findings, field.id)}
        label={`Flag “${field.label}” as a finding`}
        onChange={(finding) => onFindingsChange(replaceFinding(findings, field.id, finding))}
      />
    );
  };

  // First, render all multiplicity questions (these drive item count)
  multiplicityQuestions.forEach((field) => {
    allElements.push(
      <View key={field.id} style={styles.fieldContainer}>
        {renderField(field)}
        {renderQuestionFinding(field)}
      </View>
    );
  });
  
  // Add floor and room dropdowns after quantity questions (for single items)
  if (propertyId && (itemCount === 0 || itemCount === 1)) {
    // Add floor dropdown
    if (onFloorSelect && requiresFloor) {
      allElements.push(
        <View key="floor_single" style={styles.fieldContainer}>
          <FloorDropdown
            selectedFloor={floor}
            onFloorSelect={(selectedFloor) => {
              onFloorSelect(selectedFloor);
            }}
          />
        </View>
      );
    }

    // Add room dropdown
    if (onRoomSelect && requiresLocation) {
      allElements.push(
        <View key="room_single" style={styles.fieldContainer}>
          <RoomDropdown
            propertyId={propertyId}
            selectedRoomId={roomId}
            onRoomSelect={(selectedRoomId) => {
              // For single items, just call the callback
              onRoomSelect(selectedRoomId);
            }}
          />
        </View>
      );
    }
  }
  
  // If we have a valid count > 1, render individual item questions with sections
  if (itemCount > 1) {
    // For the first item, preserve the single room selection if available
    if (propertyId && onRoomSelect && roomId && !data['roomId_item_0']) {
      const preservedRoomData = {
        ...data,
        'roomId_item_0': roomId
      };
      setTimeout(() => onChange(preservedRoomData), 0);
    }
    
    for (let i = 0; i < itemCount; i++) {
      // Add separator for each item
      allElements.push(
        <View key={`separator_${i}`} style={styles.itemSeparator}>
          <Text style={styles.itemSeparatorText}>
            {itemCount > 1 ? `${itemName} ${i + 1}` : itemName}
          </Text>
        </View>
      );

      // Add floor dropdown for this item if onFloorSelect is provided
      if (onFloorSelect && requiresFloor) {
        const floorFieldKey = `floor_item_${i}`;
        const currentFloor = data[floorFieldKey] || (i === 0 && floor ? floor : undefined);

        allElements.push(
          <View key={`floor_${i}`} style={styles.fieldContainer}>
            <FloorDropdown
              selectedFloor={currentFloor}
              label={itemCount > 1 ? `${itemName} ${i + 1} - Floor` : 'Floor'}
              onFloorSelect={(selectedFloor) => {
                const newData = {
                  ...data,
                  [floorFieldKey]: selectedFloor
                };

                if (i === 0 && !data[floorFieldKey] && floor) {
                  newData[floorFieldKey] = selectedFloor || floor;
                }

                onChange(newData);
                onFloorSelect(selectedFloor, i);
              }}
            />
          </View>
        );
      }

      // Add room dropdown for this item if propertyId and onRoomSelect are provided
      if (propertyId && onRoomSelect && requiresLocation) {
        const roomFieldKey = `roomId_item_${i}`;
        // For the first item, inherit from the single room selection if available
        const currentRoomId = data[roomFieldKey] || (i === 0 && roomId ? roomId : undefined);
        
        allElements.push(
          <View key={`room_${i}`} style={styles.fieldContainer}>
            <RoomDropdown
              propertyId={propertyId}
              selectedRoomId={currentRoomId}
              label={itemCount > 1 ? `${itemName} ${i + 1} - Location` : 'Location'}
              onRoomSelect={(selectedRoomId) => {
                // Update data with room selection for this specific item
                const newData = {
                  ...data,
                  [roomFieldKey]: selectedRoomId
                };
                
                // If this is the first item and we haven't set individual room data yet,
                // preserve the single room value for the first item
                if (i === 0 && !data[roomFieldKey] && roomId) {
                  newData[roomFieldKey] = selectedRoomId || roomId;
                }
                
                onChange(newData);
                // Also call the parent callback if needed
                onRoomSelect(selectedRoomId, i);
              }}
            />
          </View>
        );
      }
      
      // Render all other questions for this item (if any)
      if (otherQuestions.length > 0) {
        otherQuestions.forEach((field) => {
        // For the first item, check if we should inherit the single-item value
        const itemFieldKey = `${field.id}_item_${i}`;
        const hasItemSpecificValue = data[itemFieldKey] !== undefined;
        const hasSingleValue = data[field.id] !== undefined && data[field.id] !== '';
        
        // If this is the first item and we don't have item-specific data yet,
        // but we have single-item data, copy it over
        if (i === 0 && !hasItemSpecificValue && hasSingleValue) {
          // Silently update the data to preserve the existing value for the first item
          const preservedData = {
            ...data,
            [itemFieldKey]: data[field.id]
          };
          // Use setTimeout to avoid state update during render
          setTimeout(() => onChange(preservedData), 0);
        }
        
        allElements.push(
          <View key={`${field.id}_item_${i}`} style={styles.fieldContainer}>
            {renderField(field, i)}
            {i === 0 ? renderQuestionFinding(field) : null}
          </View>
        );
        });
      }
    }
  } else if (itemCount === 1 && otherQuestions.length > 0) {
    // For single items, just show the regular questions without sectioning
    otherQuestions.forEach((field) => {
      allElements.push(
        <View key={field.id} style={styles.fieldContainer}>
          {renderField(field)}
          {renderQuestionFinding(field)}
        </View>
      );
    });
  } else if (itemCount === 0 && otherQuestions.length > 0) {
    // If count is 0, still show other questions once (without numbering)
    otherQuestions.forEach((field) => {
      allElements.push(
        <View key={field.id} style={styles.fieldContainer}>
          {renderField(field)}
          {renderQuestionFinding(field)}
        </View>
      );
    });
  }

  return (
    <View style={styles.container}>
      {allElements}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  fieldContainer: {
    marginBottom: spacing.md,
  },
  input: {
    backgroundColor: colors.background,
  },
  selectContainer: {
    marginBottom: spacing.sm,
  },
  selectLabel: {
    ...typography.body1,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  radioItem: {
    paddingLeft: 0,
  },
  multiSelectContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  chip: {
    marginRight: spacing.xs,
    marginBottom: spacing.xs,
  },
  checkboxContainer: {
    flexDirection: 'column',
    gap: spacing.xs,
  },
  checkboxOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  checkboxLabel: {
    ...typography.body1,
    color: colors.text,
    marginLeft: spacing.xs,
  },
  fileButton: {
    marginVertical: spacing.xs,
  },
  fileInfo: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    fontStyle: 'italic',
  },
  photoFieldContainer: {
    marginVertical: spacing.sm,
  },
  itemSeparator: {
    backgroundColor: colors.gray100,
    padding: spacing.md,
    marginVertical: spacing.md,
    borderRadius: 4,
  },
  itemSeparatorText: {
    ...typography.body1,
    fontWeight: 'bold',
    color: colors.text,
    textAlign: 'center',
  },
  roomLabel: {
    ...typography.body1,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
});
